import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format, differenceInDays } from "date-fns";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import UploadCredentialDialog from "@/components/UploadCredentialDialog";
import SorvVerificationDialog from "@/components/SorvVerificationDialog";
import {
  ArrowLeft, Plus, FileText, Trash, DownloadSimple, EnvelopeSimple, Phone, UsersThree, ShieldWarning,
} from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const statusFromDate = (date) => {
  const d = differenceInDays(new Date(date), new Date());
  if (d < 0) return { s: "expired", c: "red", label: `Expired ${Math.abs(d)}d ago` };
  if (d <= 30) return { s: "expiring_30", c: "red", label: `${d}d left` };
  if (d <= 60) return { s: "expiring_60", c: "yellow", label: `${d}d left` };
  if (d <= 90) return { s: "expiring_90", c: "yellow", label: `${d}d left` };
  return { s: "current", c: "green", label: `${d}d left` };
};

export default function StaffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [sorvOpen, setSorvOpen] = useState(false);
  const [sorvList, setSorvList] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/staff/${id}`);
      setData(data);
    } catch (e) {
      toast.error("Failed to load staff");
    }
    try {
      const { data: sorv } = await api.get(`/staff/${id}/sorv-verifications`);
      setSorvList(sorv);
    } catch (_) { /* non-blocking */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const removeStaff = async () => {
    await api.delete(`/staff/${id}`);
    toast.success("Staff removed");
    navigate("/staff");
  };

  const removeCred = async (credId) => {
    await api.delete(`/credentials/${credId}`);
    toast.success("Credential removed");
    load();
  };

  if (!data) return <div className="p-8 text-slate-400">Loading…</div>;

  const { staff, credentials } = data;
  const initials = staff.name.split(" ").map(n => n[0]).slice(0,2).join("");

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto">
      <Link to="/staff" data-testid="back-to-staff" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm mb-6">
        <ArrowLeft size={14} weight="bold" /> Back to Staff
      </Link>

      {/* Staff header card */}
      <div className="rounded-2xl border border-white/10 bg-slate-900 p-6 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-700 grid place-items-center text-2xl font-display font-black text-white">
            {initials}
          </div>
          <div className="flex-1">
            <div className="font-label text-orange-400 mb-1">{staff.role?.toUpperCase()}</div>
            <h1 className="text-3xl md:text-4xl font-display font-black tracking-tight text-slate-50" data-testid="staff-detail-name">
              {staff.name}
            </h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
              {staff.team && <span className="inline-flex items-center gap-1.5"><UsersThree size={14} weight="bold" />{staff.team}</span>}
              {staff.email && <span className="inline-flex items-center gap-1.5"><EnvelopeSimple size={14} weight="bold" />{staff.email}</span>}
              {staff.phone && <span className="inline-flex items-center gap-1.5"><Phone size={14} weight="bold" />{staff.phone}</span>}
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="upload-credential-button"
              onClick={() => setUploadOpen(true)}
              className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
            >
              <Plus size={16} weight="bold" className="mr-2" /> Add Credential
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 rounded-full">
                  <Trash size={16} weight="bold" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-slate-900 border-white/10 text-slate-50">
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove {staff.name}?</AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    This deletes the staff member and all their credential records. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-slate-800 border-white/10 text-slate-200 hover:bg-slate-700">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={removeStaff} className="bg-rose-500 text-slate-50 hover:bg-rose-600">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {/* Credentials */}
      <div className="rounded-2xl border border-white/10 bg-slate-900 overflow-hidden">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="font-label text-slate-500">CERTIFICATION PORTFOLIO</div>
            <h2 className="text-xl font-display font-black tracking-tight">Credentials ({credentials.length})</h2>
          </div>
        </div>

        {credentials.length === 0 ? (
          <div className="p-12 text-center">
            <FileText size={40} weight="duotone" className="text-slate-600 mx-auto mb-3" />
            <div className="text-slate-300 font-semibold">No credentials on file</div>
            <div className="text-slate-500 text-sm mt-1">Upload the first certification for {staff.name}.</div>
            <Button
              onClick={() => setUploadOpen(true)}
              className="mt-5 bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
            >
              <Plus size={16} weight="bold" className="mr-2" /> Add Credential
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {credentials.map((c) => {
              const st = statusFromDate(c.expiration_date);
              return (
                <div key={c.id} data-testid={`credential-row-${c.id}`} className="p-6 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="font-semibold text-slate-100">{c.cred_type}</div>
                      <StatusBadge status={st.s} color={st.c} />
                    </div>
                    <div className="text-sm text-slate-400 mt-1">
                      {c.issuer && <span>{c.issuer} · </span>}
                      Issued {c.issue_date ? format(new Date(c.issue_date), "MMM d, yyyy") : "—"}
                      <span className="mx-2 text-slate-600">|</span>
                      Expires {format(new Date(c.expiration_date), "MMM d, yyyy")}
                      <span className="mx-2 text-slate-600">|</span>
                      <span className={st.c === "red" ? "text-rose-400" : st.c === "yellow" ? "text-amber-400" : "text-orange-400"}>
                        {st.label}
                      </span>
                    </div>
                    {c.notes && <div className="text-xs text-slate-500 mt-2">{c.notes}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    {c.file_path && (
                      <a
                        href={`${API}/credentials/${c.id}/file`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-white/10 text-slate-300 hover:text-orange-300 hover:border-orange-500/30 transition-colors"
                      >
                        <DownloadSimple size={14} weight="bold" /> View File
                      </a>
                    )}
                    <button
                      onClick={() => removeCred(c.id)}
                      data-testid={`delete-cred-${c.id}`}
                      className="p-2 rounded-full border border-white/10 text-slate-500 hover:text-rose-400 hover:border-rose-500/30 transition-colors"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <UploadCredentialDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        staff={staff}
        onCreated={load}
      />

      {/* Sex Offender Registry Verification section */}
      <div className="rounded-2xl border border-white/10 bg-slate-900 overflow-hidden mt-6" data-testid="sorv-section">
        <div className="px-6 py-5 border-b border-white/10 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <ShieldWarning size={20} weight="bold" className="text-orange-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-label text-slate-500">SAFEGUARDING</div>
              <h2 className="text-xl font-display font-black tracking-tight">Sex Offender Registry Verification</h2>
              <p className="text-xs text-slate-400 mt-1 max-w-xl leading-relaxed">
                Search the official NSOPW registry and log the outcome. A name match alone is <span className="font-semibold text-slate-200">never</span> grounds for adverse action.
              </p>
            </div>
          </div>
          <Button
            data-testid="log-sorv-button"
            onClick={() => setSorvOpen(true)}
            className="bg-orange-500 text-white hover:bg-orange-400 font-semibold rounded-full"
          >
            <ShieldWarning size={14} weight="bold" className="mr-1.5" /> Search & Log Verification
          </Button>
        </div>

        {sorvList.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-slate-300 font-semibold">No registry verification on file</div>
            <div className="text-slate-500 text-sm mt-1">Recommended annually. Log every search — every audit will ask.</div>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {sorvList.map((v) => {
              const resultStyle = v.result === "no_apparent_match"
                ? { border: "border-emerald-500/25", bg: "bg-emerald-500/10", text: "text-emerald-300", label: "No apparent match" }
                : v.result === "possible_match_manual_review"
                ? { border: "border-rose-500/25", bg: "bg-rose-500/10", text: "text-rose-300", label: "Possible match — manual review" }
                : { border: "border-amber-500/25", bg: "bg-amber-500/10", text: "text-amber-300", label: "Unable to verify" };
              return (
                <div key={v.id} data-testid={`sorv-row-${v.id}`} className="p-5 flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${resultStyle.border} ${resultStyle.bg} ${resultStyle.text}`}>
                        {resultStyle.label}
                      </span>
                      <span className="text-sm text-slate-300 font-semibold">{v.jurisdiction}</span>
                    </div>
                    <div className="text-sm text-slate-400 mt-1.5">
                      Searched {format(new Date(v.searched_at), "MMM d, yyyy")} · by <span className="text-slate-200">{v.reviewer_name}</span>
                    </div>
                    {v.notes && <div className="text-xs text-slate-500 mt-2 leading-relaxed">{v.notes}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SorvVerificationDialog
        open={sorvOpen}
        onOpenChange={setSorvOpen}
        staff={staff}
        onCreated={load}
      />
    </div>
  );
}
